"use client";
import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { format } from "date-fns";
import { X, Calendar, Phone, Mail } from "lucide-react";
import { IComingSoonProduct } from "@/app/types/product.types";
import { PALETTE } from "@/app/lib/colorPallete";
import { useOutsideClick } from "@/app/hooks/useOutsideClick";

interface ComingSoonExpandedViewProps {
  product: IComingSoonProduct;
  onClose: () => void;
}
const ComingSoonExpandedView: React.FC<ComingSoonExpandedViewProps> = ({
  product,
  onClose,
}) => {
  const ref = useOutsideClick<HTMLDivElement>(onClose);
  const formatExpectedDate = (date?: Date) => {
    if (!date) return null;
    return format(new Date(date), "MMMM yyyy");
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, pointerEvents: "none" }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 grid place-items-center z-50 p-4"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        layout
        ref={ref}
        className="relative z-[51] w-full max-w-4xl max-h-[80vh] flex flex-col md:flex-row rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: PALETTE.cream }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
          style={{
            backgroundColor: `${PALETTE.darkGreen}20`,
            color: PALETTE.darkGreen,
          }}
        >
          <X size={20} />
        </button>

        { }
        <div className="w-full md:w-1/2 bg-stone-100 flex items-center justify-center p-6">
          <div className="relative w-full aspect-square">
            {product.commonImageUrls && product.commonImageUrls.length > 0 ? (
              <Image
                src={product.commonImageUrls[0]}
                alt={product.modelName}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            ) : (
              <div className="w-full h-full bg-stone-200 rounded-lg" />
            )}
          </div>
        </div>

        { }
        <div className="w-full md:w-1/2 p-6 overflow-y-auto">
          <h2
            className="text-3xl font-serif font-bold"
            style={{ color: PALETTE.darkGreen }}
          >
            {product.modelName}
          </h2>
          <p
            className="mt-1 text-base"
            style={{ color: `${PALETTE.darkGreen}B3` }}
          >
            {product.subtext}
          </p>

          {product.expectedDate && (
            <div className="mt-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 border border-blue-200 w-fit">
              <Calendar size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-blue-700">
                Expected: {formatExpectedDate(product.expectedDate)}
              </span>
            </div>
          )}

          <div className="my-6 border-t border-stone-200" />

          {product.description &&
            Object.keys(product.description).length > 0 && (
              <>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ color: PALETTE.darkGreen }}
                >
                  Features
                </h3>
                <div className="space-y-3 text-sm">
                  {Object.entries(product.description).map(([key, value]) => (
                    <div key={key}>
                      <p className="font-semibold text-stone-700">{key}</p>
                      <p className="text-stone-600">{value as string}</p>
                    </div>
                  ))}
                </div>
                <div className="my-6 border-t border-stone-200" />
              </>
            )}

          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: `${PALETTE.midGreen}20` }}
          >
            <h3
              className="text-xl font-semibold text-center"
              style={{ color: PALETTE.darkGreen }}
            >
              Contact for Inquiry
            </h3>
            <p className="text-center text-sm text-stone-600 mt-1">
              For pricing and availability, please get in touch.
            </p>
            <div className="mt-4 space-y-3">
              <a
                href="tel:9422080371"
                className="flex items-center gap-3 p-3 rounded-md bg-white hover:bg-stone-50 transition-colors"
              >
                <Phone size={20} style={{ color: PALETTE.darkGreen }} />
                <span className="font-semibold text-stone-800">
                  9422080371 / 9021180371
                </span>
              </a>
              <a
                href="mailto:info@fortuneagrimart.com"
                className="flex items-center gap-3 p-3 rounded-md bg-white hover:bg-stone-50 transition-colors"
              >
                <Mail size={20} style={{ color: PALETTE.darkGreen }} />
                <span className="font-semibold text-stone-800">
                  info@fortuneagrimart.com
                </span>
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ComingSoonExpandedView;
